import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Text,
  Alert,
  AlertIcon,
  HStack,
  Icon,
  Code,
  Link,
  Box,
} from '@chakra-ui/react';
import { FiExternalLink, FiSave } from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';

export default function ArriveLinkModal({ isOpen, onClose, deal, onSave }) {
  const [ariveId, setAriveId] = useState(deal?.ariveId || '');
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  const hasExistingId = deal?.ariveId && deal.ariveId.trim() !== '';

  const getAriveUrl = () => {
    if (!ariveId || ariveId.trim() === '') return null;

    // Check stage to determine URL type
    // Any post-application loan stage (Arive-aligned + legacy) opens the loan-center deep-link.
    const loanCenterStages = [
      'loan-application-intake', 'loan-app-intake',
      'loan-qualification', 'loan-preapproved',
      'loan-setup', 'loan-disclosure-sent', 'loan-submitted',
      'loan-approved-conditions', 'loan-resubmittal',
      'loan-clear-to-close', 'loan-docs-out', 'loan-docs-signed',
      'loan-funded', 'loan-brokercheck-received', 'loan-finalized',
      'loan-processing', 'deal-won', // legacy
    ];
    const isLoanCenter = loanCenterStages.includes(deal?.stage);

    if (isLoanCenter) {
      return `https://future.myarive.com/app/loans/${ariveId}/loan-center`;
    } else {
      return `https://future.myarive.com/app/leads/${ariveId}/`;
    }
  };

  const handleSave = async () => {
    if (!ariveId || ariveId.trim() === '') {
      toast({
        title: 'Arive ID required',
        description: 'Please enter an Arive Lead/Loan ID',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!isSupabaseConfigured()) {
      toast({
        title: 'Database not configured',
        description: 'Supabase is not configured',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('crm_deals')
        .update({
          arive_id: ariveId.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', deal.id);

      if (error) {
        throw error;
      }

      // Notify parent component of the update
      if (onSave) {
        onSave(ariveId.trim());
      }

      toast({
        title: 'Arive ID saved',
        description: 'Deal linked to Arive LOS',
        status: 'success',
        duration: 2000,
      });

      onClose();
    } catch (error) {
      console.error('Error saving Arive ID:', error);
      toast({
        title: 'Error saving Arive ID',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenInArive = () => {
    const url = getAriveUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiExternalLink} />
            <Text>{hasExistingId ? 'Open in Arive LOS' : 'Link to Arive LOS'}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {hasExistingId ? (
              <>
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <VStack align="flex-start" spacing={0}>
                    <Text fontSize="sm" fontWeight="semibold">
                      This deal is linked to Arive
                    </Text>
                    <Text fontSize="xs">
                      Arive ID: <Code fontSize="xs">{deal.ariveId}</Code>
                    </Text>
                  </VStack>
                </Alert>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Opening URL:
                  </Text>
                  <Link
                    href={getAriveUrl()}
                    isExternal
                    fontSize="xs"
                    color="blue.500"
                    wordBreak="break-all"
                  >
                    {getAriveUrl()}
                  </Link>
                </Box>

                <FormControl>
                  <FormLabel fontSize="sm">Update Arive ID (Optional)</FormLabel>
                  <Input
                    value={ariveId}
                    onChange={(e) => setAriveId(e.target.value)}
                    placeholder="Enter new Arive ID to update"
                    size="sm"
                  />
                </FormControl>
              </>
            ) : (
              <>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Enter the Arive Lead or Loan ID to link this deal with Arive LOS.
                    This enables quick navigation between systems.
                  </Text>
                </Alert>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Arive Lead/Loan ID</FormLabel>
                  <Input
                    value={ariveId}
                    onChange={(e) => setAriveId(e.target.value)}
                    placeholder="e.g., 12345 or LEAD-12345"
                    autoFocus
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    You can find this ID in Arive LOS URL
                  </Text>
                </FormControl>

                {ariveId && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Preview URL:
                    </Text>
                    <Code fontSize="xs" wordBreak="break-all" p={2} borderRadius="md">
                      {getAriveUrl()}
                    </Code>
                  </Box>
                )}
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          
          {hasExistingId && ariveId === deal.ariveId && (
            <Button
              colorScheme="blue"
              leftIcon={<FiExternalLink />}
              onClick={handleOpenInArive}
            >
              Open in Arive
            </Button>
          )}
          
          {(!hasExistingId || ariveId !== deal.ariveId) && (
            <Button
              colorScheme="green"
              leftIcon={<FiSave />}
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="Saving..."
            >
              Save & {hasExistingId ? 'Open' : 'Link'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

